// src/features/payment/ProviderFormModal.tsx
// Create/edit a payment provider (CreatePaymentProvider / UpdatePaymentProvider).

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow, TitleInput, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';
import type { PaymentProvider } from '@core/types/payment';
import { PaymentProviderTypeValues } from '@core/types/payment';
import { errMessage, humanizeEnum } from './format';
import { ChipListInput } from './ChipListInput';

export interface ProviderFormValue {
  name: string;
  display_name: string;
  description: string;
  type: string;
  provider_code: string;
  credential_id: string;
  supported_currencies: string;
  supported_countries: string;
}

export interface ProviderFormModalProps {
  open: boolean;
  provider: PaymentProvider | null;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: ProviderFormValue) => Promise<void>;
}

const TYPE_OPTIONS = PaymentProviderTypeValues.filter((v) => v !== 'PAYMENT_PROVIDER_TYPE_UNSPECIFIED');

const emptyForm = (): ProviderFormValue => ({
  name: '',
  display_name: '',
  description: '',
  type: TYPE_OPTIONS[0],
  provider_code: '',
  credential_id: '',
  supported_currencies: '',
  supported_countries: '',
});

export function ProviderFormModal({ open, provider, isSaving, onCancel, onSubmit }: ProviderFormModalProps) {
  const { t } = useI18n();
  const [form, setForm] = useState<ProviderFormValue>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const initialRef = useRef<ProviderFormValue>(emptyForm());
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    const next: ProviderFormValue = provider
      ? {
          name: provider.name ?? '',
          display_name: provider.display_name ?? '',
          description: provider.description ?? '',
          type: provider.type ?? TYPE_OPTIONS[0],
          provider_code: provider.provider_code ?? '',
          credential_id: provider.credential_id ?? '',
          supported_currencies: (provider.supported_currencies ?? []).join(', '),
          supported_countries: (provider.supported_countries ?? []).join(', '),
        }
      : emptyForm();
    setForm(next);
    initialRef.current = next;
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, open]);

  const change = <K extends keyof ProviderFormValue>(field: K, value: ProviderFormValue[K]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const currencies = form.supported_currencies ? form.supported_currencies.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const countries = form.supported_countries ? form.supported_countries.split(',').map((s) => s.trim()).filter(Boolean) : [];

  const canSubmitValue = !!form.name.trim() && !!form.display_name.trim() && !!form.provider_code.trim();
  const dirty = JSON.stringify(form) !== JSON.stringify(initialRef.current);

  const submit = async () => {
    if (!canSubmitValue || isSaving) return;
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(errMessage(err, t('payment.providers.saveError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onCancel}
      onSubmit={submit}
      submitLabel={provider ? t('common.saveChanges') : t('payment.providers.create')}
      canSubmit={canSubmitValue}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={provider ? t('payment.providers.edit') : t('payment.providers.create')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {error && <p className="text-sm text-destructive">{error}</p>}

        <TitleInput
          value={form.display_name}
          onChange={(v) => change('display_name', v)}
          placeholder={t('payment.providers.displayNamePlaceholder')}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('payment.providers.displayName')}
        />
        <AutoGrowTextarea
          ref={descRef}
          value={form.description}
          onChange={(v) => change('description', v)}
          placeholder={t('payment.providers.descriptionPlaceholder')}
          minRows={1}
        />

        <div className="space-y-0.5 border-t border-border pt-2">
          <FormRow label={t('payment.providers.name')} labelWidth="sm">
            <input
              value={form.name}
              onChange={(e) => change('name', e.target.value)}
              disabled={!!provider}
              placeholder={t('payment.providers.namePlaceholder')}
              autoCapitalize="off"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
          </FormRow>
          <FormRow label={t('payment.providers.type')} labelWidth="sm">
            <Select
              value={form.type}
              onChange={(e) => change('type', e.target.value)}
              disabled={!!provider}
              fullWidth={false}
              className="w-full"
              aria-label={t('payment.providers.type')}
            >
              {TYPE_OPTIONS.map((v) => (
                <option key={v} value={v}>{humanizeEnum(v, 'PAYMENT_PROVIDER_TYPE_')}</option>
              ))}
            </Select>
          </FormRow>
          <FormRow label={t('payment.providers.providerCode')} labelWidth="sm">
            <input
              value={form.provider_code}
              onChange={(e) => change('provider_code', e.target.value)}
              disabled={!!provider}
              placeholder="stripe"
              autoCapitalize="off"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
          </FormRow>
          <FormRow label={t('payment.providers.credentialId')} labelWidth="sm">
            <input
              value={form.credential_id}
              onChange={(e) => change('credential_id', e.target.value)}
              autoCapitalize="off"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('payment.providers.supportedCurrencies')} labelWidth="sm">
            <ChipListInput
              value={currencies}
              onChange={(next) => change('supported_currencies', next.join(', '))}
              placeholder={t('payment.providers.currenciesPlaceholder')}
              normalize={(raw) => raw.toUpperCase()}
              aria-label={t('payment.providers.supportedCurrencies')}
            />
          </FormRow>
          <FormRow label={t('payment.providers.supportedCountries')} labelWidth="sm">
            <ChipListInput
              value={countries}
              onChange={(next) => change('supported_countries', next.join(', '))}
              placeholder={t('payment.providers.countriesPlaceholder')}
              normalize={(raw) => raw.toUpperCase()}
              aria-label={t('payment.providers.supportedCountries')}
            />
          </FormRow>
        </div>
      </div>
    </FormDialog>
  );
}

export default ProviderFormModal;
